import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from 'core/store/authStore';
import { changeMyRole } from 'features/user/services/userApi';
import type { AppRole } from 'features/user/services/userApi';

const ROLES: {
  id: AppRole;
  label: string;
  tagline: string;
  description: string;
  icon: string;
  perks: string[];
  dashboardPath: string;
}[] = [
  {
    id: 'customer',
    label: 'Customer',
    tagline: 'Send packages & deliveries',
    description: 'Create delivery jobs, track packages in real time, and manage your order history.',
    icon: 'local_shipping',
    perks: ['Create delivery orders', 'Real-time order tracking', 'Order history & receipts'],
    dashboardPath: '/customer/dashboard',
  },
  {
    id: 'provider',
    label: 'Provider',
    tagline: 'Deliver and earn on your schedule',
    description: 'Register your vehicle, accept delivery jobs, and manage active deliveries.',
    icon: 'directions_run',
    perks: ['Browse available jobs', 'Vehicle verification', 'Delivery history & stats'],
    dashboardPath: '/provider/dashboard',
  },
];

export default function RolePage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const currentRole: AppRole | undefined =
    user?.role === 'provider' ? 'provider' : user?.role === 'customer' ? 'customer' : undefined;
  const targetRole = ROLES.find((r) => r.id === selected);

  const handleSelect = (role: AppRole) => {
    if (role === currentRole) return;
    setSelected(role);
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      await changeMyRole(selected);
      // Tokens are now revoked — force a clean logout and redirect to login
      await logout();
      navigate('/login?reason=session_updated');
    } catch (err: any) {
      setError(err?.message || 'Failed to change role. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-display">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary p-1.5 rounded-xl shadow-md shadow-primary/20">
                <span
                  className="material-symbols-outlined text-white text-[20px]"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  directions_run
                </span>
              </div>
              <span className="text-lg font-black tracking-tighter">Errandi</span>
            </Link>
            <span className="text-slate-300 text-lg">/</span>
            <span className="text-slate-600 font-medium text-sm">Change role</span>
          </div>
          <Link
            to={currentRole === 'provider' ? '/provider/dashboard' : '/customer/dashboard'}
            className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
          >
            Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Choose your role</h1>
          <p className="text-slate-500">
            Switch how you use Errandi. You can always change this later. You'll need to log in again after switching.
          </p>
        </div>

        {/* Role cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {ROLES.map((role) => {
            const isCurrent = role.id === currentRole;
            const isSelected = role.id === selected && showConfirm;

            return (
              <button
                key={role.id}
                onClick={() => handleSelect(role.id)}
                disabled={isCurrent}
                className={`
                  text-left p-5 rounded-2xl border-2 transition-all
                  ${isCurrent
                    ? 'border-primary bg-primary/5 cursor-default'
                    : isSelected
                    ? 'border-primary bg-primary/5 shadow-md shadow-primary/10'
                    : 'border-slate-200 bg-white hover:border-primary/40 hover:shadow-sm cursor-pointer'
                  }
                `}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-2.5 rounded-xl ${isCurrent || isSelected ? 'bg-primary/10' : 'bg-slate-100'}`}>
                    <span
                      className={`material-symbols-outlined text-2xl ${isCurrent || isSelected ? 'text-primary' : 'text-slate-500'}`}
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      {role.icon}
                    </span>
                  </div>
                  {isCurrent && (
                    <span className="text-xs font-semibold bg-primary text-white px-2.5 py-1 rounded-full">
                      Current
                    </span>
                  )}
                </div>

                <h3 className="font-bold text-slate-900 mb-0.5">{role.label}</h3>
                <p className="text-xs text-primary font-semibold mb-2">{role.tagline}</p>
                <p className="text-sm text-slate-500 mb-4 leading-relaxed">{role.description}</p>

                <ul className="space-y-1">
                  {role.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="material-symbols-outlined text-[14px] text-emerald-500"
                        style={{ fontVariationSettings: '"FILL" 1' }}>
                        check_circle
                      </span>
                      {perk}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Confirmation panel */}
        {showConfirm && targetRole && (
          <div className="bg-white border border-amber-200 rounded-2xl p-5">
            <div className="flex gap-3">
              <span
                className="material-symbols-outlined text-amber-500 text-xl mt-0.5 shrink-0"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                warning
              </span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">
                  Switch to <span className="text-primary">{targetRole.label}</span>?
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Your current session will end and you'll be asked to log in again. After logging in you'll be taken to the {targetRole.label} dashboard.
                </p>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
                    {error}
                  </p>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-primary-deep transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                        Switching…
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[18px]">swap_horiz</span>
                        Confirm switch
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { setShowConfirm(false); setSelected(null); }}
                    className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
